// src/pages/Separation/SeparationManagement.tsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, LayoutDashboard, FileText, AlertTriangle, ListChecks, Wallet } from 'lucide-react';
import { TabType, DashboardStats, getRolePermissions, UserRole } from '../../types/separation';
import { getSeparationStats } from '../../api/separation';
import { useAuth } from '../../contexts/AuthContext';
import DashboardStatsComponent from '../../components/separation/dashboardstats';
import ResignationList from '../../components/separation/resignationlist';
import TerminationList from '../../components/separation/terminationlist';
import ExitChecklist from '../../components/separation/exitchecklist';
import SettlementModal from '../../components/separation/settlementmodal';
import SettlementsList from '../../components/separation/settlementslist';
import ChecklistsList from '../../components/separation/checklistslist';

const SeparationManagement: React.FC = () => {
  const { user } = useAuth();
  const role = ((user?.role as string) || 'employee').toLowerCase();
  const permissions = useMemo(() => getRolePermissions(role as UserRole), [role]);
  const canViewAll = permissions.canViewAllSeparation;

  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const [isSettlementOpen, setIsSettlementOpen] = useState(false);
  const [selectedSeparation, setSelectedSeparation] = useState<{ type: string; id: string; employeeId: string } | null>(null);

  const loadStats = useCallback(async () => {
    if (!canViewAll) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getSeparationStats();
      setStats(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  }, [canViewAll]);

  // Load stats on mount so both the dashboard AND the tab count badges are
  // populated immediately (the old page only fetched on a Dashboard click).
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleViewChecklist = (type: string, id: string) => {
    setSelectedSeparation({ type, id, employeeId: '' });
    setIsChecklistOpen(true);
  };

  const handleCreateSettlement = (type: string, id: string, employeeId: string) => {
    setSelectedSeparation({ type, id, employeeId });
    setIsSettlementOpen(true);
  };

  const tabs: { id: TabType; name: string; icon: React.ReactNode; count?: number }[] = [
    { id: 'dashboard', name: 'Dashboard', icon: <LayoutDashboard size={16} /> },
    { id: 'resignations', name: 'Resignations', icon: <FileText size={16} />, count: stats?.resignations.total },
    { id: 'terminations', name: 'Terminations', icon: <AlertTriangle size={16} />, count: stats?.terminations.total },
    { id: 'checklists', name: 'Exit Checklists', icon: <ListChecks size={16} />, count: stats?.exit_checklists.pending },
    { id: 'settlements', name: 'Settlements', icon: <Wallet size={16} />, count: stats?.settlements.pending },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Separation Management</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Manage resignations, terminations, exit checklists, and full &amp; final settlements.
          </p>
        </div>
        {canViewAll && (
          <button
            onClick={loadStats}
            className="gt-btn is-ghost is-sm shrink-0"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1 flex-wrap">
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${
                  active
                    ? 'bg-white text-emerald-700 border-emerald-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {tab.icon}
                {tab.name}
                {typeof tab.count === 'number' && tab.count > 0 && (
                  <span
                    className={`ml-1 min-w-[20px] text-center text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                      active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'dashboard' && (
          <DashboardStatsComponent stats={stats} loading={loading} error={error} onRefresh={loadStats} />
        )}

        {activeTab === 'resignations' && (
          <ResignationList
            onViewChecklist={handleViewChecklist}
            onCreateSettlement={handleCreateSettlement}
            userRole={role}
            onChanged={loadStats}
          />
        )}

        {activeTab === 'terminations' && (
          <TerminationList
            onViewChecklist={handleViewChecklist}
            onCreateSettlement={handleCreateSettlement}
            userRole={role}
            canTerminate={permissions.canTerminateEmployee}
            onChanged={loadStats}
          />
        )}

        {activeTab === 'checklists' && (
          <ChecklistsList userRole={role} onOpen={handleViewChecklist} />
        )}

        {activeTab === 'settlements' && (
          <SettlementsList userRole={role} onChanged={loadStats} />
        )}
      </div>

      {/* Checklist Modal */}
      {isChecklistOpen && selectedSeparation && (
        <ExitChecklist
          separationType={selectedSeparation.type}
          separationId={selectedSeparation.id}
          isModal={true}
          onClose={() => {
            setIsChecklistOpen(false);
            setSelectedSeparation(null);
            loadStats();
          }}
        />
      )}

      {/* Settlement Modal */}
      {isSettlementOpen && selectedSeparation && (
        <SettlementModal
          isOpen={isSettlementOpen}
          onClose={() => {
            setIsSettlementOpen(false);
            setSelectedSeparation(null);
          }}
          separationType={selectedSeparation.type}
          separationId={selectedSeparation.id}
          employeeId={selectedSeparation.employeeId}
          onSuccess={() => {
            setIsSettlementOpen(false);
            setSelectedSeparation(null);
            loadStats();
          }}
        />
      )}
    </div>
  );
};

export default SeparationManagement;
